import type { ApiAddress } from '@Model';
import { EnumAddressRole } from '@Model';
import { isLinked } from '@synqo/frontend-component-library';

// What a collapsed section shows in its header, so that folding it away does not hide what it holds.
// Every summary is a single line of the values that are actually there — empty parts drop out rather
// than leaving gaps behind.

/** The name each role goes by on screen. */
export const roleLabels: Record<EnumAddressRole, string> = {
    [EnumAddressRole.Debtor]: 'Debitor',
    [EnumAddressRole.Creditor]: 'Kreditor',
    [EnumAddressRole.InterestedParty]: 'Interessent',
    [EnumAddressRole.Origin]: 'Anfallstelle',
    [EnumAddressRole.Disposal]: 'Entsorger',
    [EnumAddressRole.Carrier]: 'Beförderer',
    [EnumAddressRole.Private]: 'Privat',
};

const join = (parts: (string | undefined)[], separator = ' · ') => parts.filter(Boolean).join(separator);

/** "Berechnung: 30 Tage netto · Vergütung: Sofort" — only the payment terms that are actually linked. */
// @ai-generated 2026-09-01T08:19:03+00:00 by philipp.kammerer@synqony.com using Claude Sonnet 5
export function paymentSummary(address: ApiAddress): string {
    return join([
        isLinked(address.payment_terms_revenue)
            ? `Berechnung: ${address.payment_terms_revenue.display_string}`
            : undefined,
        isLinked(address.payment_terms_expense)
            ? `Vergütung: ${address.payment_terms_expense.display_string}`
            : undefined,
    ]);
}

/** "Erzeugernummer 123456789" — only the numbers that are there; none → empty, the header stays bare. */
// @ai-generated 2026-08-28T08:53:21+00:00 by philipp.kammerer@synqony.com using Claude Opus 5
export function registrationNumbersSummary(address: ApiAddress): string {
    const numbers: [string, string | undefined][] = [
        ['Erzeugernummer', address.producer_number],
        ['Entsorgernummer', address.disposer_number],
        ['Beförderernummer', address.carrier_number],
    ];

    return join(numbers.filter(([, value]) => value?.trim()).map(([label, value]) => `${label} ${value!.trim()}`));
}

// @ai-generated 2026-08-28T08:53:21+00:00 by philipp.kammerer@synqony.com using Claude Opus 5
export function remarksSummary(address: ApiAddress): string | undefined {
    return address.remarks?.trim() || undefined;
}

/** "Ansprechpartner (3) · Kunden (1)" — a list that is empty is left out, no lists at all say nothing. */
// @ai-generated 2026-08-28T08:53:21+00:00 by philipp.kammerer@synqony.com using Claude Opus 5
export function linksSummary(counts: { label: string; count: number }[]): string {
    const filled = counts.filter(({ count }) => count > 0);
    return join(filled.map(({ label, count }) => `${label} (${count})`));
}
